import { Link } from 'react-router';
import { Card, Icon } from '@lib/ui';

const APPROACHES = [
  {
    id: 'progressive',
    title: 'Progressive Disclosure',
    description:
      'Simple cases are simple. Complexity is additive. The recommended approach for Source modeling.',
    path: '/poc/source-modeling/progressive',
    color: 'bg-gradient-to-br from-blue-500 to-purple-600',
    recommended: true,
    features: [
      'URL + extract for simple cases',
      'Add filter/select when needed',
      'Full workflow control available',
    ],
  },
  {
    id: 'simple',
    title: 'Simple Model (Legacy)',
    description: 'Flat capabilities with static configuration. Superseded by Progressive model.',
    path: '/poc/source-modeling/simple',
    color: 'bg-emerald-500',
    recommended: false,
    features: ['Static URLs only', 'Implicit content injection', 'Capability flags'],
  },
  {
    id: 'flexible',
    title: 'Flexible Model (Legacy)',
    description: 'Dynamic inputs with tool array. Superseded by Progressive model.',
    path: '/poc/source-modeling/flexible',
    color: 'bg-blue-500',
    recommended: false,
    features: ['Static or dynamic inputs', 'Explicit templates', 'Self-contained tools'],
  },
  {
    id: 'pipeline',
    title: 'Pipeline Model (Legacy)',
    description: 'Step-based execution flow. Superseded by Progressive model.',
    path: '/poc/source-modeling/pipeline',
    color: 'bg-purple-500',
    recommended: false,
    features: ['Ordered steps', 'Explicit flow', 'Composable operations'],
  },
];

export default function SourceModelingSelector() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Source Modeling POC</h1>
          <p className="mt-2 text-gray-600 dark:text-dark-300">
            Explore different approaches to modeling Sources. The Progressive Disclosure model is
            the current direction.
          </p>
        </div>

        <div className="grid gap-6">
          {APPROACHES.map((approach) => (
            <Link key={approach.id} to={approach.path}>
              <Card
                skin="shadow"
                className={`p-6 hover:shadow-lg transition-shadow cursor-pointer group ${
                  approach.recommended
                    ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-dark-900'
                    : 'opacity-60'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`${approach.color} w-12 h-12 rounded-lg flex items-center justify-center shrink-0`}
                  >
                    <Icon name="folder" className="text-white" size="lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {approach.title}
                      </h2>
                      {approach.recommended && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-dark-300">
                      {approach.description}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {approach.features.map((feature) => (
                        <span
                          key={feature}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-dark-200"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Icon
                    name="chevron-right"
                    className="text-gray-400 group-hover:text-primary-600 transition-colors shrink-0"
                  />
                </div>
              </Card>
            </Link>
          ))}
        </div>

        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="font-medium text-blue-800 dark:text-blue-200">
            Progressive Disclosure Model
          </h3>
          <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
            Sources are defined with minimal config for simple cases. Complexity is added only when
            needed:
          </p>
          <ul className="mt-2 text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
            <li>
              <strong>Level 1:</strong> URL + extract → fetch and extract
            </li>
            <li>
              <strong>Level 2:</strong> Site + scope + extract → discover pages and extract
            </li>
            <li>
              <strong>Level 3:</strong> Add filter → discover, filter, extract
            </li>
            <li>
              <strong>Level 4:</strong> Add select → discover, filter, select one, fetch details,
              extract
            </li>
            <li>
              <strong>Level 5:</strong> Custom steps → full workflow control
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
